import { makeAutoObservable, runInAction } from 'mobx';
import { RateLimiter } from '../../utils/rate-limiter';
import { CoreDocumentsStore } from '../documents.store';
import {
    BatchUploadAppType,
    BatchUploadJob,
    BatchUploadJobStatus,
    BatchUploadMetadata,
    BatchUploadStats,
    DEFAULT_BATCH_UPLOAD_CONCURRENCY,
    DEFAULT_BATCH_UPLOAD_RATE_LIMIT,
    EnqueueBatchOptions,
    FileUploadItem,
    ICoreBatchUploadStoreConfig,
    RATE_LIMIT_RETRY_BUFFER_MS,
    RATE_LIMIT_WINDOW_MS,
    UPLOAD_FAILURE_BACKOFF_MS,
    UploadStatus,
} from './batch-upload.types';
import {
    computeUploadStats,
    createFileUploadItems,
    createUploadAbortError,
    getUploadErrorMessage,
    isUploadAborted,
} from '../../ui/batch-document-upload/upload-utils';

const createJobId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const createEmptyStats = (): BatchUploadStats => ({
    total: 0,
    success: 0,
    failed: 0,
    pending: 0,
    uploading: 0,
    cancelled: 0,
});

const isActiveJobStatus = (status: BatchUploadJobStatus): boolean =>
    status === 'queued' || status === 'running' || status === 'paused';

export class CoreBatchUploadStore {
    public batches: BatchUploadJob[] = [];
    public stats: BatchUploadStats = createEmptyStats();
    public isPaused = false;
    public isRateLimited = false;
    public rateLimitResumeAt: number | undefined;
    public uploadDelayReason: 'proactive' | 'failure_retry' | undefined;
    public isProcessing = false;

    private readonly providerDocumentsStore: CoreDocumentsStore;
    private readonly supportDocumentsStore: CoreDocumentsStore;
    private readonly maxConcurrent: number;
    private readonly rateLimiter: RateLimiter;
    private readonly onBatchComplete?: (batchId: string, items: FileUploadItem[]) => void;

    private cancelRequested = false;
    private processingPromise: Promise<void> | null = null;
    private resumeResolvers: Array<() => void> = [];
    private rateLimitWaitCount = 0;
    private readonly abortControllers = new Map<string, AbortController>();
    private readonly batchCompleteCallbacks = new Map<string, (items: FileUploadItem[]) => void>();

    constructor(config?: ICoreBatchUploadStoreConfig) {
        this.maxConcurrent = config?.maxConcurrent ?? DEFAULT_BATCH_UPLOAD_CONCURRENCY;
        this.onBatchComplete = config?.onBatchComplete;
        this.rateLimiter = new RateLimiter({
            maxRequests: config?.maxRequestsPerMinute ?? DEFAULT_BATCH_UPLOAD_RATE_LIMIT,
            windowMs: RATE_LIMIT_WINDOW_MS,
            retryBufferMs: RATE_LIMIT_RETRY_BUFFER_MS,
            createAbortError: createUploadAbortError,
        });
        this.providerDocumentsStore = new CoreDocumentsStore({ supportGrant: false });
        this.supportDocumentsStore = new CoreDocumentsStore({ supportGrant: true });
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private acquireRequestSlot = async (signal?: AbortSignal): Promise<void> => {
        let enteredRateLimitWait = false;

        try {
            await this.rateLimiter.acquire(signal, resumeAt => {
                enteredRateLimitWait = true;
                this.markRateLimitWaiting(resumeAt);
            });
        } finally {
            if (enteredRateLimitWait) {
                this.unmarkRateLimitWaiting();
            }
        }
    };

    private scheduleFailureBackoff = (): void => {
        const resumeAt = Date.now() + UPLOAD_FAILURE_BACKOFF_MS;
        this.rateLimiter.pauseUntil(resumeAt);

        runInAction(() => {
            this.isRateLimited = true;
            this.uploadDelayReason = 'failure_retry';
            this.rateLimitResumeAt = Math.max(this.rateLimitResumeAt ?? 0, resumeAt);
        });
    };

    private markRateLimitWaiting = (resumeAt: number): void => {
        runInAction(() => {
            this.rateLimitWaitCount += 1;
            this.isRateLimited = true;
            if (this.uploadDelayReason !== 'failure_retry') {
                this.uploadDelayReason = 'proactive';
            }
            this.rateLimitResumeAt = Math.max(this.rateLimitResumeAt ?? 0, resumeAt);
        });
    };

    private unmarkRateLimitWaiting = (): void => {
        runInAction(() => {
            this.rateLimitWaitCount = Math.max(0, this.rateLimitWaitCount - 1);

            if (this.rateLimitWaitCount === 0 && !this.rateLimiter.isGloballyPaused()) {
                this.isRateLimited = false;
                this.rateLimitResumeAt = undefined;
                this.uploadDelayReason = undefined;
            }
        });
    };

    private waitForGlobalBackoff = async (signal?: AbortSignal): Promise<void> => {
        let enteredWait = false;

        try {
            await this.rateLimiter.waitUntilReady(signal, resumeAt => {
                enteredWait = true;
                this.markRateLimitWaiting(resumeAt);
            });
        } finally {
            if (enteredWait) {
                this.unmarkRateLimitWaiting();
            }
        }
    };

    public get allItems(): FileUploadItem[] {
        return this.batches.flatMap(batch => batch.items);
    }

    public get isActive(): boolean {
        return this.batches.some(batch => isActiveJobStatus(batch.status));
    }

    public get completedCount(): number {
        return this.stats.success + this.stats.failed + this.stats.cancelled;
    }

    public get isUploading(): boolean {
        return (
            this.stats.uploading > 0 ||
            this.isRateLimited ||
            (this.isProcessing && this.stats.pending > 0 && !this.isPaused)
        );
    }

    public enqueueBatch = (
        files: File[],
        metadata: BatchUploadMetadata,
        appType: BatchUploadAppType,
        options?: EnqueueBatchOptions,
    ): string => {
        if (files.length === 0) {
            return '';
        }

        const job: BatchUploadJob = {
            id: createJobId(),
            appType,
            metadata,
            items: createFileUploadItems(files),
            status: 'queued',
            createdAt: Date.now(),
        };

        if (options?.onComplete) {
            this.batchCompleteCallbacks.set(job.id, options.onComplete);
        }

        runInAction(() => {
            this.batches.push(job);
            this.stats.total += job.items.length;
            this.stats.pending += job.items.length;
        });

        void this.ensureProcessing();

        return job.id;
    };

    public pause = (): void => {
        this.isPaused = true;
    };

    public resume = (): void => {
        this.isPaused = false;
        const resolvers = [...this.resumeResolvers];
        this.resumeResolvers = [];
        resolvers.forEach(resolve => resolve());
        void this.ensureProcessing();
    };

    public cancel = (): void => {
        this.cancelRequested = true;
        this.isPaused = false;

        for (const controller of this.abortControllers.values()) {
            controller.abort(createUploadAbortError());
        }

        const cancelledQueuedBatches: BatchUploadJob[] = [];

        runInAction(() => {
            for (const batch of this.batches) {
                if (!isActiveJobStatus(batch.status)) {
                    continue;
                }

                // Never-started batches won't pass through processBatch/notifyBatchComplete.
                if (batch.status === 'queued') {
                    cancelledQueuedBatches.push(batch);
                }

                for (const item of batch.items) {
                    if (item.status === 'pending') {
                        this.transitionItemStatus(batch.id, item.id, 'pending', 'cancelled');
                    } else if (item.status === 'uploading') {
                        this.transitionItemStatus(batch.id, item.id, 'uploading', 'cancelled');
                    }
                }

                batch.status = 'cancelled';
            }
        });

        const resolvers = [...this.resumeResolvers];
        this.resumeResolvers = [];
        resolvers.forEach(resolve => resolve());

        cancelledQueuedBatches.forEach(batch => this.notifyBatchComplete(batch));

        runInAction(() => {
            this.isRateLimited = false;
            this.rateLimitResumeAt = undefined;
            this.uploadDelayReason = undefined;
            this.rateLimitWaitCount = 0;
        });

        this.rateLimiter.clearPause();
    };

    public clearCompletedBatches = (): void => {
        runInAction(() => {
            this.batches = this.batches.filter(batch => isActiveJobStatus(batch.status));
            this.stats = computeUploadStats(this.batches.flatMap(batch => batch.items));
        });
    };

    public reset = (): void => {
        this.cancel();
        runInAction(() => {
            this.batches = [];
            this.stats = createEmptyStats();
            this.isProcessing = false;
            this.isRateLimited = false;
            this.rateLimitResumeAt = undefined;
            this.uploadDelayReason = undefined;
            this.rateLimitWaitCount = 0;
            this.cancelRequested = false;
            this.abortControllers.clear();
            this.batchCompleteCallbacks.clear();
        });

        this.rateLimiter.clearPause();
    };

    private async ensureProcessing(): Promise<void> {
        if (this.processingPromise) {
            return;
        }

        this.processingPromise = this.processQueue().finally(() => {
            this.processingPromise = null;
        });

        await this.processingPromise;
    }

    private async processQueue(): Promise<void> {
        runInAction(() => {
            this.isProcessing = true;
        });

        try {
            while (true) {
                if (this.cancelRequested) {
                    runInAction(() => {
                        this.cancelRequested = false;
                    });
                }

                const batch = this.getNextRunnableBatch();
                if (!batch) {
                    break;
                }

                await this.processBatch(batch);
            }
        } finally {
            runInAction(() => {
                this.isProcessing = false;
            });
        }
    }

    private getNextRunnableBatch(): BatchUploadJob | undefined {
        const inProgress = this.batches.find(
            batch =>
                (batch.status === 'running' || batch.status === 'paused') &&
                batch.items.some(item => item.status === 'pending'),
        );

        if (inProgress) {
            return inProgress;
        }

        return this.batches.find(batch => batch.status === 'queued');
    }

    private async processBatch(batch: BatchUploadJob): Promise<void> {
        runInAction(() => {
            batch.status = 'running';
        });

        const documentsStore =
            batch.appType === 'support' ? this.supportDocumentsStore : this.providerDocumentsStore;

        const pendingCount = batch.items.filter(item => item.status === 'pending').length;

        if (pendingCount === 0) {
            this.finalizeBatchStatus(batch);
            return;
        }

        const workerCount = Math.min(this.maxConcurrent, pendingCount);
        const nextIndexRef = { value: 0 };
        const workers = Array.from({ length: workerCount }, () =>
            this.runBatchWorker(batch, documentsStore, nextIndexRef),
        );

        await Promise.all(workers);

        if (this.cancelRequested) {
            runInAction(() => {
                this.cancelRequested = false;
            });
        }

        this.finalizeBatchStatus(batch);
        this.notifyBatchComplete(batch);
    }

    private notifyBatchComplete(batch: BatchUploadJob): void {
        const batchCallback = this.batchCompleteCallbacks.get(batch.id);
        this.batchCompleteCallbacks.delete(batch.id);
        batchCallback?.(batch.items);
        this.onBatchComplete?.(batch.id, batch.items);
    }

    private async runBatchWorker(
        batch: BatchUploadJob,
        documentsStore: CoreDocumentsStore,
        nextIndexRef: { value: number },
    ): Promise<void> {
        while (true) {
            if (this.cancelRequested) {
                return;
            }

            if (this.isPaused) {
                runInAction(() => {
                    if (batch.status === 'running') {
                        batch.status = 'paused';
                    }
                });
                await this.waitIfPaused();
                runInAction(() => {
                    if (batch.status === 'paused' && batch.items.some(item => item.status === 'pending')) {
                        batch.status = 'running';
                    }
                });
                continue;
            }

            let item: FileUploadItem | undefined;

            runInAction(() => {
                while (nextIndexRef.value < batch.items.length) {
                    const candidate = batch.items[nextIndexRef.value];
                    nextIndexRef.value += 1;

                    if (candidate.status !== 'pending') {
                        continue;
                    }

                    item = candidate;
                    this.transitionItemStatus(batch.id, candidate.id, 'pending', 'uploading', {
                        errorMessage: undefined,
                    });
                    break;
                }
            });

            if (!item) {
                return;
            }

            const abortController = new AbortController();
            this.abortControllers.set(item.id, abortController);

            try {
                let hasRetried = false;

                while (true) {
                    try {
                        const documentId = await documentsStore.uploadDocumentFile(item.file, {
                            documentType: batch.metadata.documentType,
                            description: batch.metadata.description,
                            claimId: batch.metadata.claimId,
                            signal: abortController.signal,
                            beforeRequest: this.acquireRequestSlot,
                        });

                        const currentItem = batch.items.find(entry => entry.id === item.id);
                        if (currentItem?.status === 'uploading') {
                            this.transitionItemStatus(batch.id, item.id, 'uploading', 'success', { documentId });
                        }

                        break;
                    } catch (error) {
                        if (isUploadAborted(error) || this.cancelRequested || abortController.signal.aborted) {
                            const currentItem = batch.items.find(entry => entry.id === item.id);
                            if (currentItem?.status === 'uploading') {
                                this.transitionItemStatus(batch.id, item.id, 'uploading', 'cancelled');
                            }
                            return;
                        }

                        if (!hasRetried) {
                            hasRetried = true;
                            this.scheduleFailureBackoff();
                            await this.waitForGlobalBackoff(abortController.signal);
                            continue;
                        }

                        const currentItem = batch.items.find(entry => entry.id === item.id);
                        if (currentItem?.status === 'uploading') {
                            this.transitionItemStatus(batch.id, item.id, 'uploading', 'failed', {
                                errorMessage: getUploadErrorMessage(error),
                            });
                        }

                        break;
                    }
                }
            } finally {
                this.abortControllers.delete(item.id);
            }
        }
    }

    private transitionItemStatus(
        batchId: string,
        itemId: string,
        fromStatus: UploadStatus,
        toStatus: UploadStatus,
        patch: Partial<FileUploadItem> = {},
    ): void {
        const batch = this.batches.find(entry => entry.id === batchId);
        const item = batch?.items.find(entry => entry.id === itemId);

        if (!batch || !item || item.status !== fromStatus) {
            return;
        }

        this.adjustStats(fromStatus, -1);
        Object.assign(item, patch, { status: toStatus });
        this.adjustStats(toStatus, 1);
    }

    private adjustStats(status: UploadStatus, delta: number): void {
        switch (status) {
            case 'pending':
                this.stats.pending += delta;
                break;
            case 'uploading':
                this.stats.uploading += delta;
                break;
            case 'success':
                this.stats.success += delta;
                break;
            case 'failed':
                this.stats.failed += delta;
                break;
            case 'cancelled':
                this.stats.cancelled += delta;
                break;
            default:
                break;
        }
    }

    private finalizeBatchStatus(batch: BatchUploadJob): void {
        runInAction(() => {
            if (batch.status === 'cancelled') {
                return;
            }

            const hasPendingOrUploading = batch.items.some(
                item => item.status === 'pending' || item.status === 'uploading',
            );

            if (hasPendingOrUploading) {
                if (this.isPaused) {
                    batch.status = 'paused';
                }
                return;
            }

            batch.status = 'completed';
        });
    }

    private waitIfPaused(): Promise<void> {
        if (!this.isPaused) {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            this.resumeResolvers.push(resolve);
        });
    }
}
